import { useEffect, useState } from 'react';
import { Loader2, MapPin, Send, X } from 'lucide-react';
import ModalOverlay from './ModalOverlay';
import { authedFetch } from '../lib/authedFetch';
import { formControl, FORM_SCOPE_CLASS } from '../utils/formTheme';
import TitleLandPicker from './TitleLandPicker';
import { canPickOnMap } from '../utils/plssPickableStates';
import { composeLandText, sortPickedSections } from '../utils/titleLandText';

/**
 * "Request title" — an agent saying where title ought to be run.
 *
 * Ordering title on a DEAL is Mays's alone and stays that way. This is the
 * other direction: an agent reading a plat or a run sheet who can see that
 * a block of acreage is worth running, with no deal to hang it on. It goes
 * to Mays as an emailed approve/deny, and only an approval opens billable
 * work in Shadowland Title.
 *
 * The land can be CLICKED ON A MAP, and can still be typed.
 *
 * Clicking is the better path when it is available: an agent reading a
 * plat knows where they mean long before they can write it as "Sec
 * 14-20S-35E", and the mistakes live in the writing — a transposed
 * township, a range that should have been East, a section that does not
 * exist in that township. None of those are visible on a form and all
 * of them are visible on a map. What the map writes is the PLSS table's
 * own spelling of the legal.
 *
 * Typing stays, and that is not a fallback for the map failing. The
 * free text is there because "the thing the agent is looking at" is
 * often finer than a section — a quarter call, a lot — or is in Texas,
 * which never went through the Public Land Survey System and has no
 * grid to click. Making somebody resolve that to a section first would
 * be asking them to do the title work in order to request the title
 * work. The map adds whole sections; everything else is written.
 */

// Paraclete's own ground. Anywhere else is a deliberate choice, so it is
// offered rather than assumed — but these two are one keystroke.
const STATES = ['NM', 'TX'];
const PRIORITIES = ['Low', 'Normal', 'High', 'Rush'];

/**
 * The deal this was raised from, if it was raised from one.
 *
 * Read off the address rather than asked for: an agent who spotted the
 * acreage while working a deal should not have to go and find its id, and
 * one who did not gets no field to puzzle over.
 */
function dealFromLocation() {
  if (typeof window === 'undefined') return { id: '', url: '' };
  const match = /^\/owners\/([^/]+)\/dashboard/.exec(window.location.pathname || '');
  if (!match) return { id: '', url: '' };
  const dealId = new URLSearchParams(window.location.search || '').get('deal') || '';
  return { id: dealId, url: window.location.href };
}

export default function TitleRequestModal({ open, onClose, zIndex = 1400 }) {
  const [state, setState] = useState('NM');
  const [county, setCounty] = useState('');
  const [legal, setLegal] = useState('');
  // The sections chosen on the map, kept apart from the typed text so
  // that un-picking one removes exactly that section and leaves
  // anything hand-written untouched.
  const [pickedSections, setPickedSections] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [owner, setOwner] = useState('');
  const [reason, setReason] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [deal, setDeal] = useState({ id: '', url: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    if (!open) return;
    setCounty('');
    setLegal('');
    setPickedSections([]);
    setPickerOpen(false);
    setOwner('');
    setReason('');
    setPriority('Normal');
    setError('');
    setDone('');
    setDeal(dealFromLocation());
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    // Both halves: the sections clicked and anything written. The map
    // can only say WHICH sections — "N/2" and "the Smith lot" live in
    // the typing — so sending one without the other points a title
    // runner at the wrong ground.
    const landText = composeLandText(pickedSections, legal);
    if (!landText) { setError('Choose the land on the map, or describe it.'); return; }
    if (!county.trim()) { setError('Which county?'); return; }
    setSending(true);
    setError('');
    try {
      const response = await authedFetch('/api/title-requests/submit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          state,
          county: county.trim(),
          legalDescription: landText,
          ownerName: owner.trim(),
          reason: reason.trim(),
          priority,
          dealId: deal.id,
          dealUrl: deal.url,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || `Could not send (${response.status})`);
      setDone(json?.warning || 'Sent to Mays. You will hear back once it is approved.');
    } catch (err) {
      setError(err?.message || 'Could not send that.');
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} zIndex={zIndex} register={false}>
      <div
        data-title-request-modal="true"
        className={FORM_SCOPE_CLASS}
        style={{ width: 'min(560px, 94vw)', background: '#ffffff', border: '1px solid #e2e8f0' }}
      >
        <div data-modal-head style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.7rem 0.9rem', background: '#003279', color: '#ffffff' }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', flex: 1 }}>
            Request title on some land
          </span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: '#cfe3f5', cursor: 'pointer', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div style={{ padding: '1.4rem 1rem', display: 'grid', gap: 12, justifyItems: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#065f46' }}>{done}</div>
            <button type="button" onClick={onClose} style={primaryButton(false)}>Close</button>
          </div>
        ) : (
          <div style={{ padding: '0.9rem', display: 'grid', gap: 10 }}>
            <div style={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.5 }}>
              Where do we need to run title? This goes to Mays to approve. If he
              approves it, an assignment opens in Shadowland Title.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8 }}>
              <Field label="State">
                <select
                  data-title-request-state
                  value={state}
                  onChange={(event) => setState(event.target.value)}
                  style={inputStyle}
                >
                  {STATES.map((code) => <option key={code} value={code}>{code}</option>)}
                </select>
              </Field>
              <Field label="County">
                <input
                  data-title-request-county
                  type="text"
                  value={county}
                  onChange={(event) => setCounty(event.target.value)}
                  placeholder="Eddy"
                  maxLength={120}
                  style={inputStyle}
                />
              </Field>
            </div>

            <Field label="The land">
              <div style={{ display: 'grid', gap: 7 }}>
                {canPickOnMap(state) ? (
                  <button
                    type="button"
                    data-title-request-pick-map
                    onClick={() => setPickerOpen(true)}
                    style={{
                      justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#eef4ff', color: '#1d4ed8', border: '1px solid #bfd4ff',
                      padding: '0.4rem 0.7rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    <MapPin size={13} />
                    {pickedSections.length
                      ? `${pickedSections.length} section${pickedSections.length === 1 ? '' : 's'} chosen — change`
                      : 'Choose on the map'}
                  </button>
                ) : (
                  // Said out loud rather than leaving a button missing.
                  // Texas has no PLSS grid to click, which is a fact
                  // about Texas and not a fault in the form.
                  <div style={{ fontSize: '0.72rem', color: '#92400e' }}>
                    {state} has no section grid to click — describe the land below.
                  </div>
                )}

                {pickedSections.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }} data-title-request-picked>
                    {sortPickedSections(pickedSections).map((row) => (
                      <span
                        key={row.featureId}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46',
                          padding: '0.18rem 0.4rem', fontSize: '0.72rem', fontWeight: 700,
                        }}
                      >
                        {row.label}
                        <button
                          type="button"
                          aria-label={`Remove ${row.label}`}
                          onClick={() => setPickedSections((prev) => prev.filter((p) => p.featureId !== row.featureId))}
                          style={{ background: 'transparent', border: 'none', color: '#065f46', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}

                <textarea
                  data-title-request-legal
                  value={legal}
                  onChange={(event) => setLegal(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={pickedSections.length
                    ? 'Anything finer than a section — N/2, a quarter call, a lot'
                    : 'Sec 14-20S-35E, N/2; Sec 15-20S-35E, NE/4'}
                  style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </Field>

            <Field label="Owner, if you know it" hint="optional">
              <input
                data-title-request-owner
                type="text"
                value={owner}
                onChange={(event) => setOwner(event.target.value)}
                placeholder="Leave blank if you are not sure"
                maxLength={200}
                style={inputStyle}
              />
            </Field>

            <Field label="Why it is worth running">
              <textarea
                data-title-request-reason
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Three operators permitting around it and nobody has run the minerals…"
                style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>

            <Field label="Priority">
              <select
                data-title-request-priority
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
                style={inputStyle}
              >
                {PRIORITIES.map((level) => <option key={level} value={level}>{level}</option>)}
              </select>
            </Field>

            {deal.url && (
              <div data-title-request-deal-note style={{ fontSize: '0.72rem', color: '#475569', lineHeight: 1.45 }}>
                Raised from the deal you have open — the approval email will link
                back to it.
              </div>
            )}

            {error && (
              <div style={{ fontSize: '0.74rem', color: '#b91c1c', fontWeight: 600 }}>{error}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={onClose} style={ghostButton}>Cancel</button>
              <button
                type="button"
                data-title-request-send
                onClick={submit}
                disabled={sending}
                style={primaryButton(sending)}
              >
                {sending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                {sending ? 'Sending…' : 'Send to Mays'}
              </button>
            </div>
          </div>
        )}
      </div>
      {pickerOpen ? (
        <TitleLandPicker
          state={state}
          picked={pickedSections}
          onPickedChange={setPickedSections}
          onClose={() => setPickerOpen(false)}
          zIndex={zIndex + 10}
        />
      ) : null}
    </ModalOverlay>
  );
}

function Field({ label, hint = '', children }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#334155' }}>
        {label}
        {hint ? <span style={{ fontWeight: 500, color: '#94a3b8' }}> · {hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

const inputStyle = formControl();

const ghostButton = {
  padding: '0.45rem 0.8rem', background: 'transparent', border: '1px solid #cbd5e1',
  borderRadius: 0, cursor: 'pointer', fontSize: '0.78rem', color: '#334155',
};

function primaryButton(busy) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '0.45rem 0.9rem', background: busy ? '#94a3b8' : '#047857',
    color: '#ffffff', border: 'none', borderRadius: 0,
    cursor: busy ? 'default' : 'pointer', fontSize: '0.78rem', fontWeight: 600,
  };
}
